import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  TextField,
  ITextField,
} from '@fluentui/react';
import { useFileBrowserStore } from '@/hooks';

// Rename a file or folder. Prefilled with the current name; for files the base
// name (excluding the extension) is pre-selected, like Windows Explorer, so you
// can retype the name without losing the extension.
export function RenameDialog() {
  const renameDialogItem = useFileBrowserStore((s) => s.renameDialogItem);
  const closeRenameDialog = useFileBrowserStore((s) => s.closeRenameDialog);
  const renameItem = useFileBrowserStore((s) => s.renameItem);

  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fieldRef = useRef<ITextField>(null);
  const item = renameDialogItem;

  useEffect(() => {
    if (!item) return;
    setName(item.name);
    setError(null);
    // Defer until the field is mounted, then focus and select the base name.
    const t = setTimeout(() => {
      fieldRef.current?.focus();
      const dot = item.isFolder ? -1 : item.name.lastIndexOf('.');
      const end = dot > 0 ? dot : item.name.length;
      fieldRef.current?.setSelectionRange(0, end);
    }, 0);
    return () => clearTimeout(t);
  }, [item]);

  if (!item) return null;

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required');
      return;
    }
    if (/[<>:"/\\|?*\x00-\x1f]/.test(trimmed)) {
      setError('Name contains invalid characters');
      return;
    }
    void renameItem(item, trimmed);
  };

  const dismiss = () => {
    setError(null);
    closeRenameDialog();
  };

  const unchanged = !name.trim() || name.trim() === item.name;

  return (
    <Dialog
      hidden={false}
      onDismiss={dismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: item.isFolder ? 'Rename Folder' : 'Rename File',
      }}
    >
      <TextField
        componentRef={fieldRef}
        label="Name"
        value={name}
        onChange={(_, value) => {
          setName(value || '');
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !unchanged) submit();
        }}
        errorMessage={error || undefined}
      />
      <DialogFooter>
        <PrimaryButton text="Rename" onClick={submit} disabled={unchanged} />
        <DefaultButton text="Cancel" onClick={dismiss} />
      </DialogFooter>
    </Dialog>
  );
}
